import React, { PropsWithChildren, useCallback } from 'react';
import { AxiosResponse } from 'axios';
import IForm from '../../../../types/IForm';
import Form, {
  FormContainerModifier,
  FormContext,
  FormResponseErrorType,
  FormUtils,
} from '../../../../components/molecules/Form';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { Editor } from '../../../../components/molecules/Form/components/Editor';
import LiveRegion from '../../../../components/atoms/LiveRegion/LiveRegion';
import { InteractionStatus } from '../../../../types/InteractionStatus';
import { murmurhash } from '../../../../lib/js/hash';
import { FormSubmitButton } from '../../../../components/molecules/Form/components/FormSubmitButton/FormSubmitButton';
import { useRefreshBlock } from '../../../BlockFetcher';
import { PostTypes } from '../Post.types';

type PostsEditorProps = {
  form: IForm;
  type: PostTypes;
  autoFocus?: boolean;
};

/**
 * Wrapper for the posts rich text editor
 */
export function PostsEditor({ form, type, autoFocus, children }: PropsWithChildren<PostsEditorProps>) {
  const { t } = useSonTranslation('Posts');
  const { delayedRefresh } = useRefreshBlock();
  const onSuccess = useCallback(
    ({ data: newPostId }: AxiosResponse<string>) => delayedRefresh(newPostId),
    [delayedRefresh],
  );

  const editorID = `posts-${type}-form-${murmurhash(form.url)}`;
  const saveButtonText = t(`default.editor.save.${type}`);

  const statusMessages = () => {
    return {
      [InteractionStatus.SUCCESS]: FormUtils.createStatusMessage(
        t('default.editor.status.success', { type: t(`default.type.${type}`) }),
      ),
      [InteractionStatus.SENDING]: FormUtils.createStatusMessage(
        t('default.editor.status.sending', { type: t(`default.type.${type}`) }),
      ),
      [InteractionStatus.FAIL]: FormUtils.createStatusMessage(
        t('default.editor.status.fail', { type: t(`default.type.${type}`) }),
      ),
      [FormResponseErrorType.MISSING_POST]: FormUtils.createStatusMessage(
        t('default.editor.status.missingPost', { type: t(`default.missingPostErrorType.${type}`) }),
      ),
    };
  };

  return (
    <div className="posts__editor">
      <Form
        key={form.url} // key needed here so form completely initializes new when form changes
        id="post-create-form"
        model={form}
        onSuccess={onSuccess}
        containerModifier={FormContainerModifier.NO_PADDING}
      >
        <FormContext.Consumer>
          {({ inputs, onChange, getStatus, submitActionEnabled }) => (
            <>
              <Editor
                value={inputs.content.value}
                name={inputs.content.name}
                id={editorID}
                constraints={inputs.content?.constraints}
                onChange={(event: any) => {
                  onChange(event.target?.name, event.target?.value);
                }}
                autoFocus={autoFocus}
              />
              {children}
              <FormSubmitButton buttonLabel={saveButtonText} enabled={submitActionEnabled()} />
              <LiveRegion invisible={false} htmlTag="div">
                {getStatus(statusMessages())}
              </LiveRegion>
            </>
          )}
        </FormContext.Consumer>
      </Form>
    </div>
  );
}
